/* sk-live-event.js — Storefront viewer controller for the Live Event Feed.
 *
 * Boot contract: a parent Liquid section sets window.skLiveEventConfig with
 *   { supabaseUrl, supabaseAnonKey, slug, shopDomain, proxyPath }
 * and renders the shell DOM (see sk-live-event-feed.liquid).
 *
 * Data shape (live_events.bracket JSONB):
 *   Multi-division (current):
 *     { brackets: [ { id, label, size: 4|8|16|32, auto_advance: bool,
 *                     matches: [...], _meta: {...} }, ... ] }
 *   Legacy single bracket (still read-compatible via normalizeBrackets):
 *     { size: 4|8|16|32, auto_advance: bool, matches: [
 *         { pos: int, team_a: str?, team_b: str?, score_a: int?, score_b: int?,
 *           court: str?, time: str? }, ...
 *       ] }
 *   pos is 1-indexed; total matches = size - 1. Round 1 = pos 1..size/2.
 *   Each division renders an independent bracket tree, stacked vertically with
 *   a Gold/Silver/Bronze accent badge (header shown only when >1 division).
 *
 * The controller fetches an initial snapshot via the App Proxy (HMAC-signed
 * by Shopify), then subscribes to Supabase Realtime for push updates. If the
 * WebSocket fails to connect within 3s or drops twice in 60s, it falls back
 * to 10s polling. Render passes are idempotent — every change triggers a
 * full re-render from the latest snapshot.
 */
(function () {
  'use strict';

  var cfg = window.skLiveEventConfig || {};
  var PROXY = cfg.proxyPath || '/apps/sk-services/live-events';
  var FALLBACK_INTERVAL_MS = 10000;
  var REALTIME_JOIN_TIMEOUT_MS = 3000;
  var REALTIME_DROP_WINDOW_MS = 60000;
  var REALTIME_MAX_DROPS_IN_WINDOW = 2;

  function $(root, sel) { return (root || document).querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fmtTime(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function fmtDateOnly(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  }

  function fetchSnapshot(slug) {
    var url = PROXY + '/read/' + encodeURIComponent(slug);
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Snapshot HTTP ' + r.status);
        return r.json();
      });
  }

  // ---------------------------------------------------------------------------
  // Divisions (Gold / Silver / +more) — multi-bracket support
  // ---------------------------------------------------------------------------

  /* Normalize the bracket JSONB into an array of division objects.
   * - New shape  { brackets: [ {id,label,size,matches,...}, ... ] } → returned as-is
   * - Legacy     { size, matches, ... }                            → wrapped as one unlabeled division
   * - Empty / not set up                                           → [] */
  function normalizeBrackets(bracket) {
    if (bracket && Array.isArray(bracket.brackets) && bracket.brackets.length) {
      return bracket.brackets;
    }
    if (bracket && bracket.size && Array.isArray(bracket.matches)) {
      return [{
        id: 'main', label: '',
        size: bracket.size, team_count: bracket.team_count,
        auto_advance: bracket.auto_advance, matches: bracket.matches,
        _meta: bracket._meta || {}
      }];
    }
    return [];
  }

  /* Accent color by division position: gold → silver → bronze → accent orange.
   * Kept in JS (not stored) so adding a division never needs a color picker. */
  var DIVISION_COLORS = ['#D4AF37', '#A8A9AD', '#CD7F32'];
  function divisionColor(idx) {
    return DIVISION_COLORS[idx] || 'var(--skle-c-accent)';
  }

  // ---------------------------------------------------------------------------
  // Bracket math (shared with admin via the same shape)
  // ---------------------------------------------------------------------------

  /* Round structure for power-of-2 single-elim:
   *   round 1: pos 1..size/2          (size/2 matches)
   *   round 2: pos size/2+1..3*size/4 (size/4 matches)
   *   round N: starts at (size - size/2^(N-1)) + 1
   * Returns array of { roundIdx (1-indexed), name, positions: int[] } */
  function buildRounds(size) {
    var rounds = [];
    var matchesInRound = size / 2;
    var posStart = 1;
    var idx = 1;
    var ROUND_NAMES_BY_TOTAL = {
      1: ['Final'],
      2: ['Semifinal','Final'],
      3: ['Quarterfinal','Semifinal','Final'],
      4: ['Round of 16','Quarterfinal','Semifinal','Final'],
      5: ['Round of 32','Round of 16','Quarterfinal','Semifinal','Final']
    };
    var totalRounds = Math.log2(size);
    var nameTable = ROUND_NAMES_BY_TOTAL[totalRounds] || [];
    while (matchesInRound >= 1) {
      var positions = [];
      for (var i = 0; i < matchesInRound; i++) positions.push(posStart + i);
      var name = nameTable[idx - 1] || ('Round ' + idx);
      rounds.push({ roundIdx: idx, name: name, positions: positions });
      posStart += matchesInRound;
      matchesInRound = Math.floor(matchesInRound / 2);
      idx++;
    }
    return rounds;
  }

  // ---------------------------------------------------------------------------
  // Header
  // ---------------------------------------------------------------------------

  function renderHeader(root, event) {
    var hero = $(root, '[data-sk-le-hero]');
    var header = (event && event.header) || {};
    var banner = header.banner_image_url || '';
    if (hero) {
      if (banner) {
        hero.style.backgroundImage = "url('" + String(banner).replace(/'/g, '%27') + "')";
        hero.setAttribute('data-has-banner', 'true');
      } else {
        hero.style.backgroundImage = '';
        hero.setAttribute('data-has-banner', 'false');
      }
    }

    var status = event.status || 'draft';
    var statusEl = $(root, '[data-sk-le-status]');
    var statusTextEl = $(root, '[data-sk-le-status-text]');
    if (statusEl) statusEl.setAttribute('data-status', status);
    if (statusTextEl) statusTextEl.textContent = (status === 'over' ? 'Final' : status).toUpperCase();

    var sportEl = $(root, '[data-sk-le-sport]');
    if (sportEl) {
      var sport = header.sport ? String(header.sport).trim() : '';
      if (sport) {
        sportEl.textContent = sport;
        sportEl.removeAttribute('hidden');
      } else {
        sportEl.textContent = '';
        sportEl.setAttribute('hidden', '');
      }
    }

    var titleEl = $(root, '[data-sk-le-title]');
    if (titleEl) titleEl.textContent = event.title || 'Live Event';

    var metaEl = $(root, '[data-sk-le-meta]');
    if (metaEl) {
      var parts = [];
      if (event.starts_at) {
        parts.push('<span class="sk-le__meta-item"><strong>' + escapeHtml(fmtDateOnly(event.starts_at)) + '</strong></span>');
      }
      if (header.location) parts.push('<span class="sk-le__meta-item">📍 ' + escapeHtml(header.location) + '</span>');
      var directorName = '';
      if (header.director) {
        if (typeof header.director === 'string') directorName = header.director;
        else if (header.director.name) directorName = header.director.name;
      }
      if (directorName) parts.push('<span class="sk-le__meta-item">Director: <strong>' + escapeHtml(directorName) + '</strong></span>');
      metaEl.innerHTML = parts.join('');
    }

    var noticeEl = $(root, '[data-sk-le-preview-notice]');
    if (noticeEl) {
      if (status === 'upcoming') {
        var when = event.starts_at ? fmtDateOnly(event.starts_at) : '';
        noticeEl.textContent = when
          ? 'Tournament begins ' + when + ' — brackets shown for preview.'
          : 'Tournament is upcoming — brackets shown for preview.';
        noticeEl.removeAttribute('hidden');
      } else {
        noticeEl.textContent = '';
        noticeEl.setAttribute('hidden', '');
      }
    }
  }

  // ---------------------------------------------------------------------------
  // LIVE NOW strip — driven by live_event_matches rows with status='live'
  // ---------------------------------------------------------------------------

  function renderLiveStrip(root, liveMatches) {
    var strip = $(root, '[data-sk-le-live-strip]');
    var rail = $(root, '[data-sk-le-live-rail]');
    if (!strip || !rail) return;
    if (root.getAttribute('data-show-live') === 'false' || !liveMatches.length) {
      strip.setAttribute('hidden', '');
      rail.innerHTML = '';
      return;
    }
    strip.removeAttribute('hidden');
    rail.innerHTML = liveMatches.map(function (m) {
      var aName = m.team_a || m.slot_a || 'A';
      var bName = m.team_b || m.slot_b || 'B';
      var aShort = aName.length > 14 ? aName.slice(0, 12) + '…' : aName;
      var bShort = bName.length > 14 ? bName.slice(0, 12) + '…' : bName;
      var scoreA = m.score_a == null ? '–' : m.score_a;
      var scoreB = m.score_b == null ? '–' : m.score_b;
      return ''
        + '<span class="sk-le__live-card">'
        +   '<span class="sk-le__live-card-court">' + escapeHtml(m.court || '') + '</span>'
        +   '<span>' + escapeHtml(aShort) + ' <span class="sk-le__live-card-score">' + escapeHtml(scoreA) + '</span></span>'
        +   '<span style="color:rgba(255,255,255,0.4);">vs</span>'
        +   '<span><span class="sk-le__live-card-score">' + escapeHtml(scoreB) + '</span> ' + escapeHtml(bShort) + '</span>'
        + '</span>';
    }).join('');
  }

  // ---------------------------------------------------------------------------
  // Bracket tree renderer (view mode)
  // ---------------------------------------------------------------------------

  /* Pure: build the inner bracket-tree HTML for ONE division.
   * Returns the placeholder string when the division has no size/matches yet. */
  function renderBracketTreeHtml(bracket) {
    var size = bracket && Number(bracket.size);
    var matches = (bracket && Array.isArray(bracket.matches)) ? bracket.matches : [];
    if (!size || !matches.length) {
      return '<div class="sk-le__bracket-empty">The bracket hasn’t been set up yet.</div>';
    }

    var rounds = buildRounds(size);
    var byPos = {};
    matches.forEach(function (m) { if (m && m.pos != null) byPos[m.pos] = m; });

    // Bracket min-height = (cells in biggest round) × per-row height, so the
    // grid rows are big enough for cell content + visible gap.
    var maxCellsInRound = rounds.reduce(function (n, r) { return Math.max(n, r.positions.length); }, 1);
    var rowHeight = 168; // px per row; matches comfortable cell-with-meta height + 30px padding
    var minHeight = maxCellsInRound * rowHeight;

    var html = '<div class="sk-le__bracket" style="min-height:' + minHeight + 'px;">';
    rounds.forEach(function (rd, rIdx) {
      var count = rd.positions.length;
      html += '<div class="sk-le__bracket-round" data-round="' + rd.roundIdx + '" style="--rn-count:' + count + ';">';
      html += '<div class="sk-le__bracket-round-head">' + escapeHtml(rd.name) + '</div>';
      rd.positions.forEach(function (pos) {
        var cellHtml = renderMatchCell(byPos[pos] || { pos: pos }, rIdx === rounds.length - 1);
        html += '<div class="sk-le-bm-wrap" data-pos="' + escapeHtml(pos) + '">' + cellHtml + '</div>';
      });
      html += '</div>';
    });
    html += '</div>';
    return html;
  }

  /* Render every division stacked into the single bracket wrap. Each division
   * gets its own horizontal-scroll container; the labeled header only shows
   * when there is more than one division (so single-bracket events look
   * exactly as before). */
  function renderBracket(root, bracket) {
    var wrap = $(root, '[data-sk-le-bracket-wrap]');
    if (!wrap) return;

    var divisions = normalizeBrackets(bracket);
    if (!divisions.length) {
      wrap.innerHTML = '<div class="sk-le__bracket-empty">The bracket hasn’t been set up yet.</div>';
      return;
    }

    var showHeads = divisions.length > 1;
    var html = divisions.map(function (div, idx) {
      var color = divisionColor(idx);
      var head = (showHeads && div.label)
        ? '<div class="sk-le__division-head"><span class="sk-le__division-badge">' + escapeHtml(div.label) + '</span></div>'
        : '';
      return ''
        + '<div class="sk-le__division" data-division-id="' + escapeHtml(div.id || ('d' + idx)) + '" style="--division-color:' + color + ';">'
        +   head
        +   '<div class="sk-le__division-scroll">' + renderBracketTreeHtml(div) + '</div>'
        + '</div>';
    }).join('');
    wrap.innerHTML = html;
  }

  function renderMatchCell(m, isFinal) {
    var a = m.team_a || '';
    var b = m.team_b || '';
    var sa = m.score_a;
    var sb = m.score_b;
    var hasScores = sa != null && sb != null;
    var winner = null;
    if (hasScores) {
      if (sa > sb) winner = 'a';
      else if (sb > sa) winner = 'b';
    }
    var champion = isFinal && winner;

    var slotA = renderSlot(a, sa, winner === 'a', champion === 'a');
    var slotB = renderSlot(b, sb, winner === 'b', champion === 'b');

    var metaBits = [];
    if (m.court) metaBits.push('<span>' + escapeHtml(m.court) + '</span>');
    if (m.time)  metaBits.push('<span>' + escapeHtml(m.time)  + '</span>');
    var meta = metaBits.length ? '<div class="sk-le-bm__meta">' + metaBits.join('') + '</div>' : '';

    var champClass = champion ? ' sk-le-bm--champion' : '';
    return ''
      + '<div class="sk-le-bm' + champClass + '" data-pos="' + escapeHtml(m.pos) + '">'
      +   slotA
      +   slotB
      +   meta
      + '</div>';
  }

  function renderSlot(name, score, isWinner, isChampion) {
    var nameHtml = name
      ? '<span class="sk-le-bm__name">' + escapeHtml(name) + '</span>'
      : '<span class="sk-le-bm__name sk-le-bm__name--tbd">—</span>';
    var scoreHtml = score == null
      ? '<span class="sk-le-bm__score" style="color:var(--skle-c-muted);">—</span>'
      : '<span class="sk-le-bm__score">' + escapeHtml(score) + '</span>';
    var winClass = isWinner ? ' sk-le-bm__slot--winner' : '';
    return '<div class="sk-le-bm__slot' + winClass + '">' + nameHtml + scoreHtml + '</div>';
  }

  // ---------------------------------------------------------------------------
  // Announcements (collapsible, defaults closed)
  // ---------------------------------------------------------------------------

  function renderAnnouncements(root, items) {
    var section = $(root, '[data-sk-le-section="news"]');
    if (!section || root.getAttribute('data-show-announcements') === 'false') {
      if (section) section.setAttribute('hidden', '');
      return;
    }
    section.removeAttribute('hidden');

    var list = $(root, '[data-sk-le-announcements]');
    var empty = $(root, '[data-sk-le-announcements-empty]');
    var count = $(root, '[data-sk-le-announcements-count]');
    var toggle = $(root, '[data-sk-le-announcements-toggle]');
    var body = $(root, '#sk-le-announcements-body');

    if (!items.length) {
      if (list) list.innerHTML = '';
      if (empty) empty.removeAttribute('hidden');
      if (count) count.textContent = '';
      if (toggle) toggle.textContent = 'Show';
      if (body) body.setAttribute('hidden', '');
      return;
    }
    if (empty) empty.setAttribute('hidden', '');
    if (count) count.textContent = items.length + (items.length === 1 ? ' post' : ' posts');

    var sorted = items.slice().sort(function (a, b) {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    if (list) {
      list.innerHTML = sorted.map(function (a) {
        var pinnedClass = a.pinned ? ' sk-le-ann--pinned' : '';
        var badge = a.pinned ? '<div class="sk-le-ann__badge">Pinned</div>' : '';
        return ''
          + '<article class="sk-le-ann' + pinnedClass + '">'
          +   badge
          +   '<div class="sk-le-ann__body">' + escapeHtml(a.body) + '</div>'
          +   '<div class="sk-le-ann__meta">' + escapeHtml(fmtTime(a.created_at)) + '</div>'
          + '</article>';
      }).join('');
    }
  }

  function bindAnnouncementsToggle(root) {
    var toggle = $(root, '[data-sk-le-announcements-toggle]');
    var body = $(root, '#sk-le-announcements-body');
    if (!toggle || !body) return;
    toggle.addEventListener('click', function () {
      var hidden = body.hasAttribute('hidden');
      if (hidden) {
        body.removeAttribute('hidden');
        toggle.textContent = 'Hide';
        toggle.setAttribute('aria-expanded', 'true');
      } else {
        body.setAttribute('hidden', '');
        toggle.textContent = 'Show';
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Master render
  // ---------------------------------------------------------------------------

  function renderAll(root, snapshot) {
    if (!snapshot || !snapshot.event) {
      root.setAttribute('data-state', 'empty');
      return;
    }
    root.setAttribute('data-state', 'ready');
    root.setAttribute('data-event-id', snapshot.event.id);

    var matches = snapshot.matches || [];
    // Suppress the "Live Now" strip while the event itself is still upcoming —
    // a match flagged 'live' in that state would be a data inconsistency, not
    // something to surface to viewers.
    var liveMatches = snapshot.event.status === 'upcoming'
      ? []
      : matches.filter(function (m) { return m.status === 'live'; });

    renderHeader(root, snapshot.event);
    renderLiveStrip(root, liveMatches);
    renderBracket(root, snapshot.event.bracket || {});
    renderAnnouncements(root, snapshot.announcements || []);
  }

  // ---------------------------------------------------------------------------
  // Realtime + polling
  // ---------------------------------------------------------------------------

  function startPolling(root, slug) {
    if (root.__skPollTimer) return;
    root.__skPollTimer = setInterval(function () {
      if (document.hidden) return;
      fetchSnapshot(slug).then(function (snap) { renderAll(root, snap); }).catch(function () { /* swallow */ });
    }, FALLBACK_INTERVAL_MS);
  }
  function stopPolling(root) {
    if (root.__skPollTimer) { clearInterval(root.__skPollTimer); root.__skPollTimer = null; }
  }

  function startRealtime(root, eventId, slug) {
    if (!window.supabase || !cfg.supabaseUrl || !cfg.supabaseAnonKey) {
      startPolling(root, slug);
      return;
    }
    var client = window.supabase.createClient(cfg.supabaseUrl, cfg.supabaseAnonKey);
    // Broadcast-from-DB (realtime.broadcast_changes) emits on a PRIVATE channel,
    // so the client must opt into private mode AND set the auth token so the
    // RLS policy on realtime.messages can evaluate as anon.
    try { client.realtime.setAuth(cfg.supabaseAnonKey); } catch (_e) { /* noop */ }
    var channel = client.channel('live-event:' + eventId, { config: { private: true } });

    var dropTimestamps = [];
    function recordDrop() {
      var now = Date.now();
      dropTimestamps = dropTimestamps.filter(function (t) { return now - t < REALTIME_DROP_WINDOW_MS; });
      dropTimestamps.push(now);
      if (dropTimestamps.length >= REALTIME_MAX_DROPS_IN_WINDOW) {
        try { channel.unsubscribe(); } catch (_e) { /* noop */ }
        startPolling(root, slug);
      }
    }
    function fetchAndRender() {
      fetchSnapshot(slug).then(function (snap) { renderAll(root, snap); }).catch(function () { /* swallow */ });
    }
    channel
      .on('broadcast', { event: 'event:update' },        fetchAndRender)
      .on('broadcast', { event: 'match:upsert' },        fetchAndRender)
      .on('broadcast', { event: 'match:delete' },        fetchAndRender)
      .on('broadcast', { event: 'announcement:new' },    fetchAndRender)
      .on('broadcast', { event: 'announcement:delete' }, fetchAndRender)
      .subscribe(function (status) {
        if (status === 'SUBSCRIBED') { root.setAttribute('data-realtime', 'on'); stopPolling(root); }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') recordDrop();
      });

    setTimeout(function () {
      if (root.getAttribute('data-realtime') !== 'on' && root.__skPollTimer == null) {
        startPolling(root, slug);
      }
    }, REALTIME_JOIN_TIMEOUT_MS);
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function resolveSlug(root) {
    var slug = root.getAttribute('data-slug');
    if (slug) return slug;
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('event_slug');
      if (fromUrl) return fromUrl;
    } catch (_e) { /* noop */ }
    return '';
  }

  function boot() {
    var roots = $$(document, '[data-sk-live-event-root]');
    if (roots.length === 0) return;
    roots.forEach(function (root) {
      var slug = resolveSlug(root);
      if (!slug) { root.setAttribute('data-state', 'empty'); return; }
      root.setAttribute('data-slug', slug);
      root.setAttribute('data-state', 'loading');
      bindAnnouncementsToggle(root);
      fetchSnapshot(slug)
        .then(function (snap) {
          renderAll(root, snap);
          if (snap && snap.event && snap.event.id) startRealtime(root, snap.event.id, slug);
          else startPolling(root, slug);
        })
        .catch(function () {
          root.setAttribute('data-state', 'error');
          startPolling(root, slug);
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose helpers for admin reuse (same render)
  window.SkLiveEvent = window.SkLiveEvent || {};
  window.SkLiveEvent.buildRounds = buildRounds;
  window.SkLiveEvent.renderMatchCell = renderMatchCell;
  window.SkLiveEvent.escapeHtml = escapeHtml;
  window.SkLiveEvent.normalizeBrackets = normalizeBrackets;
  window.SkLiveEvent.divisionColor = divisionColor;
}());
